import { getRestaurantFirebaseAuth } from "@/lib/firebase/config";
import { useQueryClient } from "@tanstack/react-query";
import { startFirebaseCredential, retryRealtimeConnection } from "@a2z/realtime/firebase";
import type { RealtimeStatus } from "@a2z/realtime";
import type { SellerSession } from "@/lib/api/types";
import { queryKeys } from "@/shared/queries";
import { useRestaurantRealtime } from "@/shared/realtime";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  firebaseSessionAdapter,
  ownRestaurantFirebaseSession,
  type FirebaseSessionAdapter,
} from "@/lib/firebase/auth";
import type { NotificationFeedAdapter } from "@/lib/firebase/notification-feed";
import type { PushRegistrationAdapter } from "./usePushRegistration";
import { useNotificationFeed } from "./useNotificationFeed";
import { usePushRegistration } from "./usePushRegistration";
import { useNotificationSound } from "./useNotificationSound";

export type FirebaseBootstrapStatus =
  | "inactive"
  | "authenticating"
  | "ready"
  | "unavailable";

type RestaurantNotificationsContextValue = ReturnType<
  typeof useNotificationFeed
> &
  ReturnType<typeof usePushRegistration> & {
    bootstrapStatus: FirebaseBootstrapStatus;
    realtimeStatus: RealtimeStatus;
    canRetryBootstrap: boolean;
    retryBootstrap: () => void;
  } & ReturnType<typeof useNotificationSound>;

const RestaurantNotificationsContext =
  createContext<RestaurantNotificationsContextValue | null>(null);

export function RestaurantNotificationsProvider({
  sellerId,
  storeId,
  firebaseToken,
  children,
  sessionAdapter = firebaseSessionAdapter,
  feedAdapter,
  pushAdapter,
}: {
  sellerId: string | undefined;
  storeId: string | undefined;
  firebaseToken: string | null | undefined;
  children: ReactNode;
  sessionAdapter?: FirebaseSessionAdapter;
  feedAdapter?: NotificationFeedAdapter;
  pushAdapter?: PushRegistrationAdapter;
}) {
  const realtimeStatus = useRestaurantRealtime(sellerId, storeId);
  const queryClient = useQueryClient();
  const [bootstrapStatus, setBootstrapStatus] =
    useState<{
      key: string;
      credentialMarker: object | null;
      status: FirebaseBootstrapStatus;
      isAuthorized?: () => boolean;
    }>({
      key: "",
      credentialMarker: null,
      status: "inactive",
    });
  const [bootstrapRetryVersion, setBootstrapRetryVersion] = useState(0);
  // Keep the short-lived Firebase custom token out of component state and
  // developer tooling. A token refresh for the same scope re-authenticates via
  // the effect dependency without changing the visible scope key.
  const authKey = sellerId && storeId ? `${sellerId}:${storeId}` : "";
  // A non-sensitive identity for this credential prop. It changes
  // synchronously with a token refresh, so the old ready/feed state closes on
  // that render without putting the custom token into React state/devtools.
  const credentialMarker = useMemo(
    () => ({ authKey, hasToken: Boolean(firebaseToken) }),
    [authKey, firebaseToken],
  );
  const resolvedBootstrapStatus: FirebaseBootstrapStatus = !sellerId || !storeId
    ? "inactive"
    : !firebaseToken
      ? bootstrapStatus.key === authKey && bootstrapStatus.status === "ready" && bootstrapStatus.isAuthorized?.()
        && (sessionAdapter !== firebaseSessionAdapter || getRestaurantFirebaseAuth()?.currentUser?.uid === sellerId)
        ? "ready" : "unavailable"
      : bootstrapStatus.key === authKey &&
          bootstrapStatus.credentialMarker === credentialMarker
        ? bootstrapStatus.status
        : "authenticating";

  const activeAuthScopeRef = useRef("");
  const readyScopeRef = useRef("");
  const preserveSessionRef = useRef(false);

  useEffect(() => {
    let active = true;
    // readSession invalidates the previous lease before publishing changed
    // permissions. A missing replacement token may preserve only a live lease.
    if (!preserveSessionRef.current) readyScopeRef.current = "";
    const owner = ownRestaurantFirebaseSession(sessionAdapter);
    const previousScope = activeAuthScopeRef.current;
    activeAuthScopeRef.current = authKey;
    const current = () => active && owner.isCurrent();
    const authenticated = () => readyScopeRef.current === authKey && (sessionAdapter !== firebaseSessionAdapter || getRestaurantFirebaseAuth()?.currentUser?.uid === sellerId);
    const stop = startFirebaseCredential({
      isCurrent: current,
      isAuthenticated: authenticated,
      signIn: async () => {
        if (!authKey) {
          readyScopeRef.current = "";
          await owner.signOut();
          return;
        }
        if (previousScope && previousScope !== authKey) {
          readyScopeRef.current = "";
          // Replacement sign-in still has to run if SDK sign-out failed.
          await owner.signOut().catch(() => undefined);
        }
        // /me may temporarily fail to mint a replacement custom token while
        // the same owner's existing SDK session is still healthy.
        let token = firebaseToken;
        if (!token && !authenticated()) {
          // Share the authorization-change remint already requested by useSession.
          // Cancelling it here would create a second /me read and discard its result.
          await queryClient.refetchQueries({ queryKey: queryKeys.session, exact: true, type: "active" }, { cancelRefetch: false });
          const inspected = queryClient.getQueryData<SellerSession>(queryKeys.session);
          if (inspected && inspected.seller.id === sellerId && inspected.seller.storeId === storeId) token = inspected.firebaseToken;
          if (!token) throw new Error("Firebase credential unavailable");
        }
        if (!current()) return;
        if (token) await owner.signIn(token);
        if (!current()) return;
        readyScopeRef.current = authKey;
        setBootstrapStatus({ key: authKey, credentialMarker, status: "ready", isAuthorized: current });
      },
      onError: () => {
        if (current()) setBootstrapStatus({ key: authKey, credentialMarker, status: "unavailable" });
      },
    });
    return () => {
      preserveSessionRef.current = owner.isCurrent();
      active = false; stop(); void owner.release().catch(() => undefined);
    };
  }, [authKey, bootstrapRetryVersion, credentialMarker, firebaseToken, sellerId, sessionAdapter, storeId, queryClient]);

  const canRetryBootstrap = Boolean(authKey);
  // An explicit retry of a failed exchange re-runs only the SDK sign-in, with
  // the custom token already in hand; it never re-reads the session. The
  // shared retry event is reserved for the listener: it re-attaches a degraded
  // one, and with no Firebase at all it drives the unconfigured catch-up read
  // (which refreshes every enabled query, the session among them) — the only
  // useful recovery where there is no SDK to exchange a credential with.
  const canExchangeCredential = sessionAdapter !== firebaseSessionAdapter || getRestaurantFirebaseAuth() !== null;
  const retryBootstrap = useCallback(() => {
    if (canRetryBootstrap && canExchangeCredential && resolvedBootstrapStatus !== "ready") {
      setBootstrapRetryVersion((version) => version + 1);
    } else retryRealtimeConnection();
  }, [canExchangeCredential, canRetryBootstrap, resolvedBootstrapStatus]);


  const sound = useNotificationSound({ sellerId, storeId });
  const feed = useNotificationFeed({
    ownerId: sellerId,
    storeId,
    enabled: resolvedBootstrapStatus === "ready",
    adapter: feedAdapter,
    onLiveItems: (items) => items.forEach((item) =>
      sound.notifySound({ ...item.data, type: item.type }),
    ),
  });
  const push = usePushRegistration({ sellerId, storeId, adapter: pushAdapter });
  const value = useMemo(
    () => ({
      ...feed,
      ...push,
      ...sound,
      bootstrapStatus: resolvedBootstrapStatus,
      realtimeStatus,
      canRetryBootstrap,
      retryBootstrap,
    }),
    [canRetryBootstrap, feed, push, sound, resolvedBootstrapStatus, realtimeStatus, retryBootstrap],
  );

  return (
    <RestaurantNotificationsContext.Provider value={value}>
      {children}
    </RestaurantNotificationsContext.Provider>
  );
}

export function useOptionalRestaurantNotifications(): RestaurantNotificationsContextValue | null {
  return useContext(RestaurantNotificationsContext);
}

export function useRestaurantNotifications(): RestaurantNotificationsContextValue {
  const value = useOptionalRestaurantNotifications();
  if (!value) {
    throw new Error(
      "useRestaurantNotifications must be used inside RestaurantNotificationsProvider",
    );
  }
  return value;
}
